/**
 * Contact Section Component
 * Contact form and CTA for lead generation
 */

import React, { useState } from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { Button } from './Button';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import toast from 'react-hot-toast';

export const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    interest: 'consulting',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission - Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // For now, we'll use mailto as a simple solution
      const interestMap: Record<string, string> = {
        consulting: 'OKR Consulting',
        managed: 'Managed OKR Services',
        software: 'OKR Software Only',
        unsure: 'Need Guidance'
      };
      const subject = `OKR Hub Discovery Call Request - ${interestMap[formData.interest] || 'General Inquiry'}`;
      const body = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Company: ${formData.company}
Interest: ${interestMap[formData.interest] || formData.interest}

Message:
${formData.message}
      `.trim();

      window.location.href = `mailto:info@hr4india.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      toast.success('Thank you! Opening your email client...');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        interest: 'consulting',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section id="contact" background="primary" padding="xl">
      <div className="max-w-4xl mx-auto text-center mb-16">
        <SectionHeader
          title="Start with One Clear Conversation"
          description="You don't need a full rollout to begin. You need clarity on what matters most. Let's start there."
          align="center"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Left Column - Contact Info */}
        <div>
          <h3 className="font-display text-3xl text-ink mb-6">
            Ready to Transform How You Execute?
          </h3>

          <p className="text-xl text-ink-light mb-8 leading-relaxed">
            Book a discovery call to discuss your organization's challenges and explore which OKR Hub offering is the right fit for you.
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-ink mb-1">Email Us</h4>
                <a href="mailto:info@hr4india.com" className="text-primary-600 hover:text-primary-700">
                  info@hr4india.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <Phone className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-ink mb-1">Call Us</h4>
                <a href="tel:+919820682052" className="text-primary-600 hover:text-primary-700">
                  +91 982 068 2052
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-ink mb-1">Visit Us</h4>
                <p className="text-ink-muted">
                  Mumbai, Maharashtra, India
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border-2 border-primary-200">
            <h4 className="font-semibold text-ink mb-3">What You'll Get</h4>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-ink-light">
                <span className="text-primary-500 mt-0.5">✓</span>
                <span>A deep-dive conversation about your current challenges</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-ink-light">
                <span className="text-primary-500 mt-0.5">✓</span>
                <span>Clarity on whether OKRs are the right solution</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-ink-light">
                <span className="text-primary-500 mt-0.5">✓</span>
                <span>Honest guidance on the best path forward</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-ink-light">
                <span className="text-primary-500 mt-0.5">✓</span>
                <span>No pressure, no sales pitch—just practical advice</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-ink-light">
                <span className="text-primary-500 mt-0.5">✓</span>
                <span>A recommendation for consulting, managed services, or software</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right Column - Contact Form */}
        <div className="bg-white rounded-2xl p-8 shadow-soft-lg">
          <h3 className="font-display text-2xl text-ink mb-6">Schedule a Discovery Call</h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors"
                placeholder="Your name"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors"
                  placeholder="+91 123 456 7890"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-ink mb-2">
                Company Name
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors"
                placeholder="Your company"
              />
            </div>

            <div>
              <label htmlFor="interest" className="block text-sm font-medium text-ink mb-2">
                I'm Interested In *
              </label>
              <select
                id="interest"
                name="interest"
                required
                value={formData.interest}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors"
              >
                <option value="consulting">OKR Consulting</option>
                <option value="managed">Managed OKR Services</option>
                <option value="software">OKR Software Only</option>
                <option value="unsure">Not Sure Yet - Need Guidance</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-ink mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary-500 focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your requirements..."
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>Scheduling...</>
              ) : (
                <>
                  Book Discovery Call
                  <Send className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>

            <p className="text-xs text-ink-muted text-center">
              By submitting this form, you agree to our privacy policy and terms of service.
            </p>
          </form>
        </div>
      </div>
    </Section>
  );
};
